<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, minLength, required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import NextButton from 'dashboard/components-next/button/Button.vue';

const requiredCurrentPassword = helpers.withMessage(
  'Preencha com a sua senha',
  required
);
const requiredNewPassword = helpers.withMessage(
  'Cadastre sua nova senha',
  required
);
const requiredPasswordConfirmation = helpers.withMessage(
  'Repita a senha inserida',
  required
);
const minPasswordLength = helpers.withMessage(
  'A senha deve conter ao menos 8 caracteres',
  minLength(8)
);
const hasSpecialChar = helpers.withMessage(
  'A senha deve conter ao menos 1 caractere especial',
  value => !helpers.req(value) || /[^A-Za-z0-9]/.test(value)
);
const hasUppercase = helpers.withMessage(
  'A senha deve conter ao menos 1 letra maiúscula',
  value => !helpers.req(value) || /[A-Z]/.test(value)
);
const matchesPassword = helpers.withMessage(
  'As duas senhas não são iguais.',
  function matchesPasswordValue(value) {
    return !helpers.req(value) || value === this.password;
  }
);
const invalidPasswordMessage = 'Senha inválida';
const errorToText = error => {
  const parsedError = parseAPIErrorResponse(error);
  if (Array.isArray(parsedError)) {
    return parsedError.join(' ');
  }
  if (parsedError && typeof parsedError === 'object') {
    return JSON.stringify(parsedError);
  }
  return parsedError?.toString?.() || '';
};
const passwordErrorMessage = error => {
  const message = errorToText(error);
  const normalizedMessage = message.toLowerCase();

  if (
    !message ||
    normalizedMessage.includes('current password') ||
    normalizedMessage.includes('invalid') ||
    normalizedMessage.includes('incorrect') ||
    normalizedMessage.includes('senha invalida') ||
    normalizedMessage.includes('senha inválida')
  ) {
    return invalidPasswordMessage;
  }

  return message;
};

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
      isPasswordChanging: false,
      errorMessage: '',
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    currentPassword: {
      required: requiredCurrentPassword,
    },
    password: {
      required: requiredNewPassword,
      minLength: minPasswordLength,
      hasSpecialChar,
      hasUppercase,
    },
    passwordConfirmation: {
      required: requiredPasswordConfirmation,
      isEqPassword: matchesPassword,
    },
  },
  computed: {
    currentPasswordError() {
      return this.v$.currentPassword.$errors[0]?.$message || '';
    },
    passwordError() {
      return this.v$.password.$errors[0]?.$message || '';
    },
    passwordConfirmationError() {
      return this.v$.passwordConfirmation.$errors[0]?.$message || '';
    },
    isButtonDisabled() {
      return (
        !this.currentPassword ||
        !this.password ||
        !this.passwordConfirmation ||
        this.v$.currentPassword.$invalid ||
        this.v$.password.$invalid ||
        this.v$.passwordConfirmation.$invalid
      );
    },
  },
  methods: {
    async changePassword() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      let alertMessage = this.$t('PROFILE_SETTINGS.PASSWORD_UPDATE_SUCCESS');
      try {
        await this.$store.dispatch('updatePassword', {
          password: this.password,
          passwordConfirmation: this.passwordConfirmation,
          currentPassword: this.currentPassword,
        });
      } catch (error) {
        alertMessage = passwordErrorMessage(error);
      } finally {
        useAlert(alertMessage);
      }
    },
  },
};
</script>

<template>
  <form @submit.prevent="changePassword()">
    <div class="flex flex-col w-full gap-4">
      <woot-input
        v-model="currentPassword"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.currentPassword.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')"
        :error="currentPasswordError"
        @input="v$.currentPassword.$touch"
        @blur="v$.currentPassword.$touch"
      />

      <woot-input
        v-model="password"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.password.$error }"
        label="Nova senha*"
        placeholder="Cadastre sua nova senha"
        :error="passwordError"
        @input="v$.password.$touch"
        @blur="v$.password.$touch"
      />

      <woot-input
        v-model="passwordConfirmation"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.passwordConfirmation.$error }"
        label="Repita a senha*"
        placeholder="Repita a senha inserida"
        :error="passwordConfirmationError"
        @input="v$.passwordConfirmation.$touch"
        @blur="v$.passwordConfirmation.$touch"
      />

      <div>
        <NextButton
          type="submit"
          :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT')"
          :disabled="isButtonDisabled"
        />
      </div>
    </div>
  </form>
</template>
